import { cur } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BankAccountSummaryWidget({ bankAccountSummary }) {
  const navigate = useNavigate();
  if (!bankAccountSummary || !bankAccountSummary.accounts?.length) return null;

  return (
    <Card className="border-info/30 bg-gradient-to-br from-blue-50/50 to-white" data-testid="bank-account-summary">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <Building2 size={18} className="text-info" /> Bank Account Summary
          </CardTitle>
          <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={() => navigate('/bank-accounts')} data-testid="manage-bank-accounts-btn">
            Manage Accounts
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1" data-testid="bank-summary-scope-note">
          Same totals as the Bank Accounts page — uploaded statements when present, else sales + expenses + supplier payments.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {bankAccountSummary.accounts.map(acc => (
            <div key={acc.id} className={`p-4 rounded-xl border transition-all ${acc.is_default ? 'bg-info/10 border-info/30' : 'bg-white border-border'}`} data-testid={`bank-summary-${acc.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-info/20 flex items-center justify-center">
                  <Building2 size={16} className="text-info" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{acc.name}</div>
                  <div className="text-[10px] text-muted-foreground">{acc.assigned_branch} {acc.is_default ? '(Default)' : ''}</div>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-success">Received</span>
                  <span className="font-bold text-success">{cur()} {fmt(acc.incoming)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-error">Paid Out</span>
                  <span className="font-bold text-error">{cur()} {fmt(acc.outgoing)}</span>
                </div>
                <div className="flex justify-between text-sm pt-1.5 border-t">
                  <span className="font-medium">Calculated balance</span>
                  <span className={`font-bold ${acc.net >= 0 ? 'text-success' : 'text-error'}`}>{cur()} {fmt(acc.net)}</span>
                </div>
                <div className="text-[10px] text-amber-700 font-medium" data-testid={`bank-summary-unverified-${acc.id}`}>UNVERIFIED · not available funds until reconciled</div>
                <div className="text-[10px] text-muted-foreground">{acc.sales_count} bank sales</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 p-3 rounded-xl bg-info/20 border border-info/30 flex flex-wrap justify-between items-center gap-2">
          <span className="text-sm font-semibold text-info">All Banks Total <span className="text-[10px] font-medium text-amber-700">(calculated · UNVERIFIED)</span></span>
          <div className="flex gap-4 text-sm">
            <span className="text-success">In: <strong>{cur()} {fmt(bankAccountSummary.total_bank_incoming)}</strong></span>
            <span className="text-error">Out: <strong>{cur()} {fmt(bankAccountSummary.total_bank_outgoing)}</strong></span>
            <span className={bankAccountSummary.total_bank_net >= 0 ? 'text-success' : 'text-error'}>Net: <strong>{cur()} {fmt(bankAccountSummary.total_bank_net)}</strong></span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
